function asMessage(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  if (item.message) return item.message;
  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
}

function messageLines(items = []) {
  return items.map(asMessage).map((line) => line.trim()).filter(Boolean);
}

function formatCountLine(run) {
  return [
    `found ${run.recordsFound ?? 0}`,
    `created ${run.recordsCreated ?? 0}`,
    `updated ${run.recordsUpdated ?? 0}`,
    `failed ${run.recordsFailed ?? 0}`,
    `skipped ${run.recordsSkipped ?? 0}`,
  ].join(", ");
}

function formatDuration(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return "unknown";
  const value = Number(ms);
  if (value < 1000) return `${value}ms`;
  const seconds = Math.round(value / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function bulletList(lines, empty = "None recorded.") {
  if (!lines.length) return empty;
  return lines.map((line) => `- ${line}`).join("\n");
}

function formatLogLine(entry) {
  const time = entry.createdAt || "";
  const step = entry.step || "step";
  const level = entry.level || "info";
  const message = entry.message || "";
  const context =
    entry.context && Object.keys(entry.context).length
      ? ` ${JSON.stringify(entry.context)}`
      : "";
  return `[${level}] ${time} ${step}: ${message}${context}`.trim();
}

export function isIngestionProblem(run) {
  if (!run) return false;
  const status = String(run.status || "").toUpperCase();
  const runStatus = String(run.runStatus || "").toLowerCase();
  return (
    status === "FAILED" ||
    runStatus === "failed" ||
    runStatus === "completed_with_errors" ||
    (run.errorCount ?? 0) > 0 ||
    (run.logErrorCount ?? 0) > 0 ||
    (run.recordsFailed ?? 0) > 0 ||
    Boolean(run.errorMessage)
  );
}

export function summarizeIngestionIssue(run) {
  if (!run) return "";
  const errors = messageLines(run.errors);
  return (
    run.errorMessage ||
    errors[0] ||
    run.logSummary ||
    (run.logErrorCount ? `${run.logErrorCount} step-log error(s)` : "") ||
    (run.recordsFailed ? `${run.recordsFailed} failed row(s)` : "") ||
    ""
  );
}

export function buildIngestionIssueReport({
  run,
  logs = [],
  logCounts = {},
  pageUrl = "",
  copiedAt = new Date().toISOString(),
} = {}) {
  if (!run) return "";

  const errorLines = messageLines(run.errors);
  const warningLines = messageLines(run.warnings);
  const problemLogs = (logs || []).filter((entry) =>
    ["error", "warn", "warning"].includes(String(entry.level || "").toLowerCase())
  );
  const infoLogs = (logs || []).filter(
    (entry) => !["error", "warn", "warning"].includes(String(entry.level || "").toLowerCase())
  );
  const truncatedInfo = infoLogs.length > 25 ? infoLogs.slice(-25) : infoLogs;

  const sections = [
    "## Production ingestion issue",
    "",
    `- Copied at: ${copiedAt}`,
    `- Environment: ${pageUrl || "(unknown)"}`,
    `- Source: ${run.sourceName || run.sourceKey || "unknown"} (\`${run.sourceKey || ""}\`)`,
    `- Run ID: \`${run.runId || run.id || ""}\``,
    `- Status: ${run.status || run.runStatus || "unknown"} (${run.runStatus || "n/a"})`,
    `- Triggered by: ${run.triggeredBy || "unknown"}`,
    `- Started: ${run.startedAt || "unknown"}`,
    `- Finished: ${run.finishedAt || "unknown"}`,
    `- Duration: ${formatDuration(run.durationMs)}`,
    `- Records: ${formatCountLine(run)}`,
    `- Step logs: ${logCounts.total ?? run.logEventCount ?? logs.length} total, ${
      logCounts.errors ?? run.logErrorCount ?? 0
    } error, ${logCounts.warnings ?? run.logWarnCount ?? 0} warn`,
    `- Error: ${run.errorMessage || errorLines[0] || "none recorded"}`,
    "",
    "### Errors",
    bulletList(errorLines.length ? errorLines : run.errorMessage ? [run.errorMessage] : []),
    "",
    "### Warnings",
    bulletList(warningLines),
  ];

  if (run.logSummary) {
    sections.push("", "### Log summary", run.logSummary);
  }

  if (run.stackTrace) {
    sections.push("", "### Stack trace", "```", run.stackTrace, "```");
  }

  sections.push(
    "",
    "### Step logs (errors and warnings)",
    problemLogs.length
      ? problemLogs.map((entry) => `- ${formatLogLine(entry)}`).join("\n")
      : "None recorded."
  );

  if (truncatedInfo.length) {
    sections.push(
      "",
      `### Recent info logs${infoLogs.length > truncatedInfo.length ? " (last 25)" : ""}`,
      truncatedInfo.map((entry) => `- ${formatLogLine(entry)}`).join("\n")
    );
  }

  sections.push(
    "",
    "Use this report to reproduce and fix the collector in development. Do not treat this as a live production change."
  );

  return sections.join("\n");
}

export function buildIngestionIssuesDigest({
  reports = [],
  pageUrl = "",
  copiedAt = new Date().toISOString(),
} = {}) {
  const heading = [
    "# Production ingestion issues",
    "",
    `- Copied at: ${copiedAt}`,
    `- Environment: ${pageUrl || "(unknown)"}`,
    `- Issue count: ${reports.length}`,
    "",
  ];

  if (!reports.length) {
    return `${heading.join("\n")}No failing ingestion runs matched the current filters.`;
  }

  return `${heading.join("\n")}${reports.map((report, index) => `### ${index + 1}\n\n${report}`).join("\n\n")}`;
}

export async function copyTextToClipboard(text) {
  if (!text) {
    throw new Error("Nothing to copy.");
  }

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard is not available.");
  }

  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(area);
  if (!ok) {
    throw new Error("Unable to copy to the clipboard.");
  }
}
